import * as React from 'react';
import {
  autobind,
  BaseComponent
} from 'office-ui-fabric-react/lib/Utilities';
import {
  BasePickerListBelow,
  BasePicker,
  IBasePickerProps,
  IBasePicker
} from 'office-ui-fabric-react/lib/Pickers';
import {
  PeoplePickerProvider,
  IPeoplePickerProviderResults,
  IPeoplePickerQueryParams,
  IPeoplePickerProvider,
  IPerson
} from '@ms/odsp-datasources/lib/PeoplePicker';
import { ICalloutProps } from 'office-ui-fabric-react/lib/Callout';
import PrincipalType from '@ms/odsp-datasources/lib/dataSources/roleAssignments/PrincipalType';
import { SuggestionItemDefault, SelectedItemDefault, SelectedItemBelowDefault } from './PeoplePickerDefaultItems';
import { IPeoplePickerProps, PeoplePickerType } from './PeoplePicker.Props';
import './PeoplePicker.scss';
import stringsResx from './PeoplePicker.resx';

export interface IPeoplePickerState {
  hasUnresolvedText?: boolean;
  searchForMoreText?: string;
}

export interface IPeoplePicker {
  /** Set focus to the input */
  focusInput: () => void;
  selectedPeople: IPerson[];
}

const PersonPicker = BasePicker as new (props: IBasePickerProps<IPerson> & { pickerCalloutProps?: ICalloutProps }) =>
  BasePicker<IPerson, IBasePickerProps<IPerson> & { pickerCalloutProps?: ICalloutProps }>;
declare class PersonPickerClass extends BasePickerListBelow<IPerson, IBasePickerProps<IPerson> & { pickerCalloutProps?: ICalloutProps }> { }
const PersonPickerListBelow: typeof PersonPickerClass = BasePickerListBelow;
export class PeoplePicker extends BaseComponent<IPeoplePickerProps &
{ pickerCalloutProps?: ICalloutProps }, IPeoplePickerState> implements IPeoplePicker {
  private _dataProvider: IPeoplePickerProvider;
  private _peoplePickerSearchPromise: any; // tslint:disable-line:no-any
  private _peoplePicker: IBasePicker<IPerson> | undefined;
  private _handleGetMoreResultsInBaseClass: boolean;

  constructor(props: (IPeoplePickerProps & { pickerCalloutProps?: ICalloutProps })) {
    super(props);

    if (props.dataProvider) {
      this._dataProvider = props.dataProvider;
    } else {
      if (!props.context) {
        throw new Error('No pageContext available for PeoplePickerProvider.');
      }

      this._dataProvider = new PeoplePickerProvider({ pageContext: props.context, peoplePickerDataSource: props.dataSource });
    }

    // People Picker base class only handle GetMoreResults when the PeoplePickerProvider has canUseGraph function,
    // canUseGraph is true, and no pass in onGetMoreResults callback.
    this._handleGetMoreResultsInBaseClass = !!(this._dataProvider.canUseGraph && this.props.peoplePickerQueryParams &&
      this._dataProvider.canUseGraph(this.props.peoplePickerQueryParams)) && !this.props.onGetMoreResults;
    this.state = {
      hasUnresolvedText: false,
      searchForMoreText: this._getSearchForMoreText(this._handleGetMoreResultsInBaseClass)
    };
  }

  public get selectedPeople(): IPerson[] {
    return (this._peoplePicker && this._peoplePicker.items) || [];
  }

  public render(): JSX.Element {
    let {
      className,
      onRenderItem,
      onRenderSuggestionsItem,
      defaultSelectedItems,
      onSelectedPersonasChange,
      suggestionsHeaderText,
      suggestionsClassName,
      noResultsFoundText,
      loadingText,
      inputProps,
      onSuggestionSelected,
      suggestionsAvailableAlertText,
      enableSelectedSuggestionAlert,
      selectedItems,
      itemLimit,
      onInputChange,
      onGetMoreResults,
      searchForMoreText,
      pickerCalloutProps } = this.props;
    let pickerProps: (IBasePickerProps<IPerson> & { pickerCalloutProps?: ICalloutProps }) = {
      className: className,
      onRenderSuggestionsItem: onRenderSuggestionsItem ? onRenderSuggestionsItem : SuggestionItemDefault,
      onResolveSuggestions: this._onResolveSuggestions,
      defaultSelectedItems: defaultSelectedItems,
      selectedItems: selectedItems,
      onChange: onSelectedPersonasChange,
      inputProps: inputProps,
      enableSelectedSuggestionAlert: enableSelectedSuggestionAlert,
      pickerSuggestionsProps: {
        suggestionsHeaderText: suggestionsHeaderText,
        loadingText: loadingText,
        noResultsFoundText: noResultsFoundText,
        className: suggestionsClassName,
        suggestionsAvailableAlertText: suggestionsAvailableAlertText,
        searchForMoreText: searchForMoreText ? searchForMoreText :
          (this._handleGetMoreResultsInBaseClass ? this.state.searchForMoreText : undefined)
      },
      onItemSelected: onSuggestionSelected,
      itemLimit: itemLimit,
      onInputChange: onInputChange,
      onGetMoreResults: onGetMoreResults ? onGetMoreResults : (this._handleGetMoreResultsInBaseClass ? this._onGetMoreResults : undefined),
      pickerCalloutProps: pickerCalloutProps
    };

    switch (this.props.peoplePickerType) {
      case PeoplePickerType.listBelow:
        pickerProps.onRenderItem = onRenderItem ? onRenderItem : SelectedItemBelowDefault;
        return (
          <PersonPickerListBelow
            { ...pickerProps }
            componentRef={ this._resolveRef('_peoplePicker') }
          />
        );
      default:
        pickerProps.onRenderItem = onRenderItem ? onRenderItem : SelectedItemDefault;
        return (
          <PersonPicker
            { ...pickerProps }
            componentRef={ this._resolveRef('_peoplePicker') }
          />
        );
    }
  }

  public focusInput(): void {
    if (this._peoplePicker) {
      this._peoplePicker.focusInput();
    }
  }

  @autobind
  private _onResolveSuggestions(value: string): PromiseLike<IPerson[]> | IPerson[] {

    if (this._peoplePickerSearchPromise) {
      this._peoplePickerSearchPromise.cancel();
      this._peoplePickerSearchPromise = undefined;
    }
    // Whitespace on ends should not go into search
    value = value.trim();
    // 1 character returns too many results so we should only search if there are 2 or more characters.
    // This is inline with the current way that the peoplepicker works in odsp-next.
    if (value.length < 2) {
      return this.props.onResolvedSuggestions ? this.props.onResolvedSuggestions([], value) : [];
    }

    let peoplePickerSearchResult: IPeoplePickerProviderResults =
      this._dataProvider.search(value.trim(), this._getPeoplePickerQueryParams());

    if (!peoplePickerSearchResult) {
      return this.props.onResolvedSuggestions ? this.props.onResolvedSuggestions([], value) : [];
    }

    let cachedItems: IPerson[] = peoplePickerSearchResult.cached ? peoplePickerSearchResult.cached : [];

    this._peoplePickerSearchPromise = peoplePickerSearchResult.promise;
    return this._peoplePickerSearchPromise.then((personList: IPerson[]) => {
      this._peoplePickerSearchPromise = undefined;
      return personList ? cachedItems.concat(personList) : [];
    })
      .then((suggestions: IPerson[]) => {
        return this.props.onResolvedSuggestions
          ? this.props.onResolvedSuggestions(suggestions, value)
          : suggestions;
      });
  }

  /**
   * Use the picker query params set on the props so that we're using updated values based on GetSharingInformation.
   * If, for some reason, no picker query params are set on the props, use default values as a fallback.
   */
  private _getPeoplePickerQueryParams(): IPeoplePickerQueryParams {
    if (this.props.peoplePickerQueryParams) {
      if (!this.props.peoplePickerQueryParams.onToggleGetMoreResults && this._handleGetMoreResultsInBaseClass) {
        this.props.peoplePickerQueryParams.onToggleGetMoreResults = this._onToggleGetMoreResults;
      }
      return this.props.peoplePickerQueryParams;
    }

    return {
      allowEmailAddresses: false,
      groupID: 0,
      maximumEntitySuggestions: 30,
      // Corresponds to all sources server side.
      principalSource: 15,
      blockExternalUsers: true,
      // The enum this variable is based off of is bitwise so we can use bitwise "or" to allow for multiple
      // prinicpalTypes being returned from the search.
      // tslint thinks that all bitwise opperators are mistyped (e.g. && as & or || as |).
      // So we need to turn it off for this.
      // tslint:disable-next-line:no-bitwise
      principalType: (PrincipalType.user | PrincipalType.securityGroup | PrincipalType.sharePointGroup)
    };
  }

  private _onToggleGetMoreResults(showGetMoreResults: boolean): void {
    if (this.state && ((showGetMoreResults && !this.state.searchForMoreText) || (!showGetMoreResults && this.state.searchForMoreText))) {
      this.setState({ searchForMoreText: this._getSearchForMoreText(showGetMoreResults) });
    }
  }

  @autobind
  private _getSearchForMoreText(showGetMoreResults: boolean): string | undefined {
    return showGetMoreResults ? stringsResx.searchForMoreText : undefined;
  }

  @autobind
  private _onGetMoreResults(query: string): IPerson[] | PromiseLike<IPerson[]> {
    let peoplePickerQueryParams: IPeoplePickerQueryParams | undefined;
    if (this.props.peoplePickerQueryParams) {
      peoplePickerQueryParams = { ...this.props.peoplePickerQueryParams };
      peoplePickerQueryParams.useGraph = false;
      peoplePickerQueryParams.useSubstrate = false;
    }

    return this._dataProvider && this._dataProvider.sharePointSearch && peoplePickerQueryParams ?
      this._dataProvider.sharePointSearch(query, peoplePickerQueryParams) : [];
  }
}

export default PeoplePicker;
